import React, { useEffect, useState } from "react";
import { Typography, Accordion, AccordionDetails, AccordionSummary, Box, Button, Tooltip, Divider, Chip } from '@mui/material';
import { apiConstants, get, getGeneric, post } from "../../common/constants";
import { parseDateAndTime } from "../../helpers/UtilityHelper";
import { exclude } from "../orders/list2";
import { ExpandMoreOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import AwbLinkCell from "../../common/AwbLinkCell";
import { awbTextSx } from "../orders/list2/cellStyles";
import OrderView from "./OrderView";
import withLayout from "../../hocs/Layout";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import MUIModal from "../../common/MUIModal";
import Reply from "./Reply";
import PageTitle from "../../common/PageTitle";
import Loader from "../../common/Loader";
import SendIcon from '@mui/icons-material/Send';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { useSnackbar } from "notistack";
import { pageContainerSx } from '../../styles/patterns';
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, BORDER, SUBTLE, WHITE, LIGHT_BG, PRIMARY_BLUE, ERROR, withOpacity } from '../../styles/foundation/colors';
import { statusChipSx } from './statusStyles';

// Icon-badge card header, extending the visual language from the new list/create-ticket
// screens (rounded white card, tinted icon badge, title + subtitle) to this detail page.
const SectionAccordion = ({ icon, title, subtitle, defaultExpanded, children }) => (
    <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={{
        border: `1px solid ${BORDER}`, borderRadius: '12px !important', mb: 2, overflow: 'hidden',
        '&::before': { display: 'none' },
    }}>
        <AccordionSummary expandIcon={<ExpandMoreOutlined sx={{ color: SUBTLE }} />} sx={{ px: 2.25, py: 1.25, bgcolor: LIGHT_BG }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 34, height: 34, borderRadius: '9px', flexShrink: 0, bgcolor: withOpacity(PRIMARY_BLUE, 0.1), color: PRIMARY_BLUE,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 18 },
                }}>{icon}</Box>
                <Box>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>{title}</Typography>
                    {subtitle && <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.2 }}>{subtitle}</Typography>}
                </Box>
            </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2.25, bgcolor: WHITE }}>{children}</AccordionDetails>
    </Accordion>
);

// One label/value pair in the Ticket Summary grid.
const Field = ({ label, value, color }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: '10px', fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
        <Box sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: color || NAVY, mt: 0.3 }}>{value ?? '—'}</Box>
    </Box>
);

const View = () => {
    const [resData, setResData] = useState()
    const [open, setOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [reload, setReload] = useState(0)
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const excludes = exclude;
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const awb = queryParams.get('awb');
    const ticketId = queryParams.get('ticketId');
    const [statusList, setStatusList] = useState([]);
    const { enqueueSnackbar } = useSnackbar()

    const columns = [
        {
            field: 'id', headerAlign: "left", headerName: 'Ticket ID', flex: 0.3, minWidth: 100,
            align: 'left', renderCell: (params) => {
                const { row } = params;
                return <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: PRIMARY_BLUE }}>#{row?.id}</Typography>
            },
        },
        {
            field: 'awb', headerAlign: "left", flex: 0.4, headerName: 'AWB(s)',
            align: 'left', renderCell: (params) => {
                const { row } = params;
                return (
                    <>

                        <AwbLinkCell awb={row.custom_fields?.cf_awb_number387695} sx={awbTextSx} fallback="-" />

                    </>
                )

            },
        },
        {
            field: 'createdBy', headerAlign: "left", flex: 0.4, headerName: 'Created By',
            align: 'left', renderCell: (params) => {
                const { row } = params;
                return <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: NAVY }}>{row.custom_fields?.cf_user_name ? row.custom_fields?.cf_user_name : "-"}</Typography>
            },
        },
        {
            field: 'categorySubcategory', headerAlign: "left", flex: 0.8, headerName: 'Category',
            align: 'left', renderCell: (params) => {
                const { row } = params;
                const category = row.custom_fields?.cf_category
                const subcategory = row.custom_fields?.cf_sub_category
                if (!category) return <Typography sx={{ fontSize: SIZE.xs, color: SUBTLE }}>—</Typography>
                return (
                    <Box sx={{ py: 0.5 }}>
                        <Chip size="small" label={category} sx={{ fontFamily: FONT, fontSize: '0.68rem', fontWeight: WEIGHT.bold, bgcolor: withOpacity(PRIMARY_BLUE, 0.1), color: PRIMARY_BLUE }} />
                        {subcategory && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5, pl: 0.25 }}>
                                <SubdirectoryArrowRightIcon sx={{ fontSize: 12, color: SUBTLE, flexShrink: 0 }} />
                                <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: SUBTLE, lineHeight: 1.2 }}>{subcategory}</Typography>
                            </Box>
                        )}
                    </Box>
                )
            },
        },
        {
            field: 'timeline',
            headerAlign: "left",
            flex: 0.6,
            headerName: 'Timeline',
            align: 'left',
            renderCell: (params) => {
                const { row } = params;
                const kSx = { fontFamily: FONT, fontSize: '9px', fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.03em' }
                const vSx = (color) => ({ fontFamily: FONT, fontSize: '0.7rem', fontWeight: WEIGHT.semibold, color: color || NAVY, mt: 0.15, whiteSpace: 'nowrap' })
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, width: '100%' }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={kSx}>Created</Typography>
                            <Typography sx={vSx()}>{parseDateAndTime(row?.created_at, true)}</Typography>
                            <Typography sx={{ ...kSx, mt: 0.4 }}>Updated</Typography>
                            <Typography sx={vSx()}>{parseDateAndTime(row?.updated_at, true)}</Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={kSx}>FTR Due</Typography>
                            <Typography sx={vSx(ERROR)}>{parseDateAndTime(row?.fr_due_by, true)}</Typography>
                            <Typography sx={{ ...kSx, mt: 0.4 }}>Resolution Due</Typography>
                            <Typography sx={vSx(ERROR)}>{parseDateAndTime(row?.due_by, true)}</Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: 'status', headerAlign: "left", flex: 0.55, headerName: 'Ticket Status',
            align: 'left', renderCell: (params) => {
                const { row } = params;
                const status = statusList.find((elem) => elem.id === row?.status)
                if (!status) return null
                return (
                    <Tooltip title={status.label_for_customers}>
                        <Chip size="small" label={status.label_for_customers} sx={statusChipSx(status.id)} />
                    </Tooltip>
                )
            },
        },
        {
            field: 'action', headerAlign: "center", flex: 0.2, headerName: 'Action',
            align: 'center', renderCell: (params) => {
                const { row } = params;
                return (
                    <Tooltip title="View ticket">
                        <Button size="small" startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
                            sx={{ fontFamily: FONT, textTransform: 'none', color: PRIMARY_BLUE, fontSize: SIZE['2xs'] }}
                            onClick={() => { view(row?.id, row.custom_fields?.cf_awb_number387695) }}>
                            View
                        </Button>
                    </Tooltip>
                )
            },
        },
    ]
    const navigate = useNavigate()
    const view = (id, awb) => {
        navigate(`/escalation_management/view?awb=${awb}&ticketId=${id}`)
    }
    const fetchData = async () => {
        setIsLoading(true);
        try {
            const apiCalls = [
                //API 1
                get(`${apiConstants.BASE_LOCAL}${apiConstants.GET_TICKETID_FRESHDESK}?ticketId=${ticketId}`)
                    .then((response) => response?.data?.responseObj ? JSON.parse(response.data.responseObj) : null)
                    .catch((error) => {
                        console.error('Error in API 1:', error);
                        return null;
                    })
            ]
            if (awb !== "null" && awb.trim() !== "" && awb !== undefined) {
                apiCalls.push(
                    // API 2
                    post(`${apiConstants.BASE_LOCAL}${apiConstants.ORDER_LISTS}`, {
                        excludes,
                        andfilter: [
                            {
                                field: 'awbNumber',
                                operator: 'eq',
                                value: awb,
                            },
                        ],
                    }).then((response) => response)
                        .catch((error) => {
                            console.error('Error in API 2:', error);
                            return null;
                        }),
                    post(`${apiConstants.BASE_LOCAL}${apiConstants.FILTER_FRESHDESK}`, {
                        awbnumber_list: [awb]
                    }).then((res) => JSON.parse(res.data?.responseObj))
                        .catch((error) => {
                            console.error('Error in API 3:', error);
                            return null;
                        }),
                )
            }
            const [ticketSummary, orderSearch, filterData] = await Promise.all(apiCalls);
            setResData({
                ticketSummary,
                orderSearch: orderSearch || null,
                filterData: filterData || null
            });
        } catch (error) {
            console.error('Error fetching data:', error);
            // toast("Error fetching data!", { type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId, awb, excludes, reload]);

    const handleReply = () => {
        setOpen(true)
    }
    const handleClose = () => {
        setOpen(false)
    }

    const otherTickets = resData?.filterData?.results?.filter((val) => val.id !== Number(ticketId))

    useEffect(() => {
        const fetchStatusList = async () => {
            try {
                const response = await getGeneric(`${apiConstants.GET_FRESHDESK_STATUS_LIST}`);
                const list = JSON.parse(response?.data?.responseObj);
                setStatusList(list?.choices);

            } catch (error) {
                enqueueSnackbar("Some error occurred in fetching status list!", { variant: "error" });
                console.error(error);
            }
        }
        fetchStatusList()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const ticketStatus = statusList.find((elem) => elem.id === resData?.ticketSummary?.status)

    const parseHtmlContent = (htmlString) => {
        const doc = new DOMParser().parseFromString(htmlString, 'text/html');
        const h4Element = doc.querySelector('h4');
        return h4Element ? h4Element.textContent : '';
    };

    const parseDivContent = (htmlString) => {
        const doc = new DOMParser().parseFromString(htmlString, 'text/html');
        const divElement = doc.querySelector('div');
        return divElement ? divElement.textContent : '';
    };

    const ts = resData?.ticketSummary
    const description = ts?.description_text || ''

    return (
        <>
            <MUIModal open={open} handleClose={handleClose} title={"Add a reply"} children={
                <Reply ticketId={ticketId} handleClose={handleClose} setReload={setReload} emails={ts?.cc_emails} />
            } />
            <Box sx={pageContainerSx}>
                {isLoading && <Loader />}
                <PageTitle>Ticket Summary / {ticketId}</PageTitle>

                {/* Header */}
                <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '11px', flexShrink: 0,
                            background: `linear-gradient(135deg, ${PRIMARY_BLUE} 0%, ${NAVY} 100%)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: `0 4px 14px ${withOpacity(PRIMARY_BLUE, 0.35)}`,
                        }}>
                            <ForumOutlinedIcon sx={{ color: WHITE, fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.lg, fontWeight: WEIGHT.extra, color: NAVY }}>Ticket #{ticketId}</Typography>
                            {awb && awb !== 'null' && <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mt: 0.2 }}>AWB {awb}</Typography>}
                        </Box>
                    </Box>
                    {ticketStatus && <Chip label={ticketStatus.label_for_customers} sx={{ ...statusChipSx(ticketStatus.id), height: 26, fontSize: '0.75rem' }} />}
                </Box>

                <SectionAccordion icon={<AssignmentOutlinedIcon />} title="Ticket Details" subtitle="Overall summary of the ticket" defaultExpanded>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2.5, mb: 2.5 }}>
                        <Field label="AWB Number" value={ts?.custom_fields?.cf_awb_number387695} />
                        <Field label="Category" value={ts?.custom_fields?.cf_category} />
                        <Field label="Sub Category" value={ts?.custom_fields?.cf_sub_category} />
                        <Field label="Created By" value={ts?.custom_fields?.cf_user_name ? `${ts.custom_fields.cf_user_name} | ${ts.custom_fields.cf_user_email}` : null} />
                        <Field label="Created Date" value={ts?.created_at ? parseDateAndTime(ts.created_at, "yyyy-MM-dd") : null} />
                        <Field label="Updated At" value={ts?.updated_at ? parseDateAndTime(ts.updated_at, "yyyy-MM-dd") : null} />
                        <Field label="First Response Date" value={ts?.fr_due_by ? parseDateAndTime(ts.fr_due_by) : null} color={ERROR} />
                        <Field label="Due By" value={ts?.due_by ? parseDateAndTime(ts.due_by) : null} color={ERROR} />
                    </Box>

                    <Divider sx={{ mb: 2.5 }} />

                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: '10px', fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>Subject</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY }}>{ts?.subject || '—'}</Typography>
                    </Box>

                    <Box sx={{ mb: 2.5 }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: '10px', fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>Description</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, lineHeight: 1.5 }}>
                            {description.length > 90 ? (
                                <>
                                    {description.substring(0, 90)}...
                                    <Tooltip title={description} arrow>
                                        <Box component="span" sx={{ borderBottom: `1px dashed ${PRIMARY_BLUE}`, color: PRIMARY_BLUE, cursor: 'pointer', ml: 0.5, fontWeight: WEIGHT.semibold }}>
                                            Read more
                                        </Box>
                                    </Tooltip>
                                </>
                            ) : (description || '—')}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {ts?.attachments?.length > 0 && (
                            <Box>
                                <Typography sx={{ fontFamily: FONT, fontSize: '10px', fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>Attachments</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {ts.attachments.map((val, index) => (
                                        <Chip key={val.id} component="a" href={val.attachment_url} target="_blank" rel="noopener noreferrer" clickable
                                            icon={<AttachFileOutlinedIcon sx={{ fontSize: 14 }} />} label={`Link ${index + 1}`} size="small"
                                            sx={{ fontFamily: FONT, fontSize: '0.68rem', color: PRIMARY_BLUE, bgcolor: withOpacity(PRIMARY_BLUE, 0.08) }} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {ts?.cc_emails?.length > 0 && (
                            <Box>
                                <Typography sx={{ fontFamily: FONT, fontSize: '10px', fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>Additional Emails</Typography>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: NAVY }}>{ts.cc_emails.join(', ')}</Typography>
                            </Box>
                        )}
                    </Box>
                </SectionAccordion>

                <SectionAccordion icon={<ForumOutlinedIcon />} title="Ticket Comments & Replies" subtitle="Conversation history for this ticket" defaultExpanded>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button disabled={ts?.status === 5} size="small" startIcon={<SendIcon sx={{ fontSize: 15 }} />} onClick={handleReply} variant="contained"
                            sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: WEIGHT.semibold, borderRadius: '8px', bgcolor: PRIMARY_BLUE, '&:hover': { bgcolor: PRIMARY_BLUE } }}>
                            Add a Reply
                        </Button>
                    </Box>
                    {ts?.conversations?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxHeight: 360, overflow: 'auto' }}>
                            {ts.conversations
                                .slice()
                                .reverse()
                                .filter(message => !message.private)
                                .map((message) => (
                                    <Box key={message.id} sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.5, bgcolor: LIGHT_BG }}>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: PRIMARY_BLUE }}>
                                                {parseHtmlContent(message.body) || "Prozo : HelpDesk"}
                                            </Typography>
                                            <Typography sx={{ fontFamily: FONT, fontSize: '0.68rem', color: SUBTLE, flexShrink: 0 }}>{parseDateAndTime(message.updated_at)}</Typography>
                                        </Box>
                                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, lineHeight: 1.5 }}>
                                            {message?.body.includes('|tikprzautoProd') ? parseDivContent(message.body).replace('|tikprzautoProd', '') : ` ${parseDivContent(message.body)}`}
                                        </Typography>
                                        {message?.attachments?.length > 0 && (
                                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                {message.attachments.map((val, index) => (
                                                    <Chip key={val.id} component="a" href={val.attachment_url} target="_blank" rel="noopener noreferrer" clickable
                                                        icon={<LinkOutlinedIcon sx={{ fontSize: 13 }} />} label={`Link ${index + 1}`} size="small"
                                                        sx={{ fontFamily: FONT, fontSize: '0.65rem', color: PRIMARY_BLUE, bgcolor: withOpacity(PRIMARY_BLUE, 0.08) }} />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                        </Box>
                    ) : (
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, textAlign: 'center', py: 2 }}>No messages</Typography>
                    )}
                </SectionAccordion>

                <SectionAccordion icon={<LocalShippingOutlinedIcon />} title="Shipment (AWB) Details" subtitle="Details of the shipment this ticket was raised for">
                    {resData && (resData?.orderSearch?.data?.count === 1 && resData?.orderSearch?.data?.data[0]?.awb_number === awb) ?
                        <OrderView resData={resData} />
                        : <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, textAlign: 'center', py: 2 }}>No Data</Typography>
                    }
                </SectionAccordion>

                {otherTickets && otherTickets.length > 0 && (
                    <SectionAccordion icon={<AssignmentOutlinedIcon />} title="Other Related Tickets" subtitle="Other tickets raised against the same AWB">
                        <Box sx={{
                            '& .MuiDataGrid-root': { border: 'none' },
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: LIGHT_BG, borderBottom: `1px solid ${BORDER}` },
                            '& .MuiDataGrid-columnHeaderTitle': { fontFamily: FONT, fontWeight: WEIGHT.extra, fontSize: '0.7rem', color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.03em' },
                            '& .MuiDataGrid-cell': { borderBottom: `1px solid ${BORDER}`, fontFamily: FONT, fontSize: '0.8rem' },
                        }}>
                            <ProzoDataGrid
                                columns={columns}
                                rows={otherTickets || []}
                                pagination={true}
                                sizePerPage={sizePerPage}
                                setSizePerPage={setSizePerPage}
                                setPage={setPage}
                                page={page}
                                hideFooterPagination={false}
                                paginationMode={"client"}
                                rowHeight={120}
                                rowCount={otherTickets?.length || 0}
                                getRowId={(row) => row.id}
                            />
                        </Box>
                    </SectionAccordion>
                )}
            </Box>
        </>
    );
};

export default withLayout(View)
